import { getMessages } from "@/i18n/runtime";
import type {
  HoverIdleSnapshot,
  HovCellGainUpgradeIndex,
  HovGainUpgradeOneCostBatchPreview,
  HovGainUpgradeOneCostBatchSummary,
  HovGainUpgradeSimultaneousPurchasePreview,
} from "../types";
import type { HugeNumber } from "@/shared/numbers";
import type {
  GroupingHovGainUpgradeEntry,
  GroupingHovGainUpgradeRowState,
  HovCellGainUpgradeEntry,
} from "./groupingHovGainUpgradePanelView";
import {
  buildHovGainUpgradeWallBlockedTooltipViewModel,
  buildHovGainUpgradeTooltipViewModel,
  createLazyHovGainUpgradeTooltipViewModel,
  formatHovGainUpgradeActionCost,
  formatHovGainUpgradeBlockedActionCost,
  formatHovGainUpgradeMilestoneMultiplier,
  type HovGainUpgradeTooltipKind,
} from "./hovGainUpgradeTooltipViewModel";
import { isUpgradeMilestonePurchase } from "../core/upgradeMilestones";

const HG_TOOLTIP_ID = "hover-idle-grouping-hov-gain-upgrade-tooltip-global";
const H1G_TOOLTIP_ID = "hover-idle-grouping-hov-gain-upgrade-tooltip-cell-1";
const HOV_CELL_GAIN_UPGRADE_DISPLAY_INDICES = [
  9, 8, 7, 6, 5, 4, 3, 2,
] as const satisfies readonly HovCellGainUpgradeIndex[];

export interface GroupingHovGainUpgradeCatalogOptions {
  readonly resolveGlobalOneCostBatchPreview?: () =>
    | HovGainUpgradeOneCostBatchPreview
    | undefined;
  readonly resolveHovCellOneCostBatchPreview?: (
    index: HovCellGainUpgradeIndex,
  ) => HovGainUpgradeOneCostBatchPreview | undefined;
}

export function buildGroupingHovGainUpgradeCatalog(
  options: GroupingHovGainUpgradeCatalogOptions = {},
): readonly GroupingHovGainUpgradeEntry[] {
  const messages = getMessages();
  const upgradeTooltip = messages.hovView.upgradeTooltip;

  return [
    ...HOV_CELL_GAIN_UPGRADE_DISPLAY_INDICES.map((index) =>
      createCellEntry(index),
    ),
    {
      kind: "cell",
      id: "h1g",
      index: 1,
      label: "H1G",
      cardAriaLabel: "H1G",
      tooltip: {
        id: H1G_TOOLTIP_ID,
        title: upgradeTooltip.cell.title(1),
        region: messages.hovView.region,
        summary: upgradeTooltip.cell.summary(1),
        effectLabel: upgradeTooltip.effectLabel,
        afterLabel: upgradeTooltip.cell.afterLabel(1),
        costLabel: upgradeTooltip.costLabel,
        stateLabel: upgradeTooltip.stateLabel,
      },
      render: (snapshot) => renderH1GRow(snapshot),
    },
    {
      kind: "global",
      id: "hg",
      label: "HG",
      cardAriaLabel: messages.hovView.upgradeCardAria,
      tooltip: {
        id: HG_TOOLTIP_ID,
        title: upgradeTooltip.global.title,
        region: messages.hovView.region,
        summary: upgradeTooltip.global.summary,
        effectLabel: upgradeTooltip.effectLabel,
        afterLabel: upgradeTooltip.global.afterLabel,
        costLabel: upgradeTooltip.costLabel,
        stateLabel: upgradeTooltip.stateLabel,
      },
      render: (snapshot) => renderHGRow(snapshot),
    },
  ];

  function createCellEntry(
    index: HovCellGainUpgradeIndex,
  ): HovCellGainUpgradeEntry {
    const cellIndex = index - 1;
    const label = `H${index}G`;
    const tooltipId = `hover-idle-grouping-hov-gain-upgrade-tooltip-cell-${cellIndex}`;
    return {
      kind: "cell",
      id: label.toLowerCase(),
      index,
      label,
      cardAriaLabel: label,
      tooltip: {
        id: tooltipId,
        title: upgradeTooltip.cell.title(index),
        region: messages.hovView.region,
        summary: upgradeTooltip.cell.summary(index),
        effectLabel: upgradeTooltip.effectLabel,
        afterLabel: upgradeTooltip.cell.afterLabel(index),
        costLabel: upgradeTooltip.costLabel,
        stateLabel: upgradeTooltip.stateLabel,
      },
      render: (snapshot) => renderCellRow(snapshot, cellIndex),
    };
  }

  function renderHGRow(
    snapshot: HoverIdleSnapshot,
  ): GroupingHovGainUpgradeRowState {
    const blockedReason = snapshot.hovUpgradePurchaseBlockedReason;
    const batchSummary = snapshot.hovUpgradeOneCostBatchPreview;
    const simultaneousPreview =
      batchSummary === undefined
        ? snapshot.hovUpgradeSimultaneousPurchasePreview
        : undefined;
    const milestoneReady =
      simultaneousPreview === undefined &&
      snapshot.canPurchaseHovUpgrade &&
      isUpgradeMilestonePurchase(snapshot.hovUpgradeEnhancementLevel);
    return {
      isVisible: true,
      isAffordable: snapshot.canPurchaseHovUpgrade,
      isMilestoneReady: milestoneReady,
      costText:
        blockedReason !== undefined
          ? formatHovGainUpgradeBlockedActionCost(blockedReason)
          : formatHovGainUpgradeActionCost({
              cost: snapshot.hovUpgradeCost,
              purchaseCount: batchSummary?.purchaseCount,
              simultaneousPurchase: toSimultaneousPurchase(simultaneousPreview),
            }),
      tooltip: createLazyHovGainUpgradeTooltipViewModel(() => {
        if (blockedReason !== undefined) {
          return buildHovGainUpgradeWallBlockedTooltipViewModel({
            kind: { type: "global" },
            enhancementLevel: snapshot.hovUpgradeEnhancementLevel,
            reason: blockedReason,
          });
        }
        const tooltipBatchPreview = resolveTooltipBatchPreview(
          batchSummary,
          options.resolveGlobalOneCostBatchPreview,
        );
        return buildHovGainUpgradeTooltipViewModel({
          kind: { type: "global" },
          currentHov: snapshot.hov,
          cost: snapshot.hovUpgradeCost,
          canPurchase: snapshot.canPurchaseHovUpgrade,
          currentValue: snapshot.hovGainPerSecond,
          nextValue:
            simultaneousPreview?.valueAfter ??
            resolveTooltipBatchValueAfter(tooltipBatchPreview) ??
            snapshot.hovGainPerSecondAfterUpgrade,
          effect: resolveTooltipEffect(milestoneReady, tooltipBatchPreview),
          purchaseCount: tooltipBatchPreview?.purchaseCount,
          simultaneousPurchase: toSimultaneousPurchase(simultaneousPreview),
          enhancementLevel: snapshot.hovUpgradeEnhancementLevel,
        });
      }),
    };
  }

  function renderH1GRow(
    snapshot: HoverIdleSnapshot,
  ): GroupingHovGainUpgradeRowState {
    const cell = snapshot.hovTargetCells[0];
    if (!cell?.upgradeAvailable) {
      return invisibleRow();
    }
    const blockedReason = cell.upgradePurchaseBlockedReason;
    const batchSummary = cell.upgradeOneCostBatchPreview;
    const simultaneousPreview =
      batchSummary === undefined
        ? cell.upgradeSimultaneousPurchasePreview
        : undefined;
    const milestoneReady =
      simultaneousPreview === undefined &&
      cell.canPurchase &&
      isUpgradeMilestonePurchase(cell.enhancementLevel);
    return {
      isVisible: true,
      isAffordable: cell.canPurchase,
      isMilestoneReady: milestoneReady,
      costText:
        blockedReason !== undefined
          ? formatHovGainUpgradeBlockedActionCost(blockedReason)
          : formatHovGainUpgradeActionCost({
              cost: cell.upgradeCost,
              purchaseCount: batchSummary?.purchaseCount,
              simultaneousPurchase: toSimultaneousPurchase(simultaneousPreview),
            }),
      tooltip: createLazyHovGainUpgradeTooltipViewModel(() => {
        if (blockedReason !== undefined) {
          return buildHovGainUpgradeWallBlockedTooltipViewModel({
            kind: cellTooltipKind(0),
            enhancementLevel: cell.enhancementLevel,
            reason: blockedReason,
          });
        }
        const tooltipBatchPreview = resolveTooltipBatchPreview(
          batchSummary,
          () => options.resolveHovCellOneCostBatchPreview?.(1),
        );
        return buildHovGainUpgradeTooltipViewModel({
          kind: cellTooltipKind(0),
          currentHov: snapshot.hov,
          cost: cell.upgradeCost,
          canPurchase: cell.canPurchase,
          currentValue: cell.effectiveHovGainPerSecond,
          nextValue:
            simultaneousPreview?.valueAfter ??
            resolveTooltipBatchValueAfter(tooltipBatchPreview) ??
            cell.effectiveHovGainPerSecondAfterUpgrade,
          effect: resolveTooltipEffect(milestoneReady, tooltipBatchPreview),
          purchaseCount: tooltipBatchPreview?.purchaseCount,
          simultaneousPurchase: toSimultaneousPurchase(simultaneousPreview),
          enhancementLevel: cell.enhancementLevel,
        });
      }),
    };
  }

  function renderCellRow(
    snapshot: HoverIdleSnapshot,
    cellIndex: number,
  ): GroupingHovGainUpgradeRowState {
    const cell = snapshot.hovTargetCells[cellIndex];
    if (!cell?.upgradeAvailable) {
      return invisibleRow();
    }
    const affordable = cell.canPurchase;
    const blockedReason = cell.upgradePurchaseBlockedReason;
    const batchSummary = cell.upgradeOneCostBatchPreview;
    const simultaneousPreview =
      batchSummary === undefined
        ? cell.upgradeSimultaneousPurchasePreview
        : undefined;
    const milestoneReady =
      simultaneousPreview === undefined &&
      affordable &&
      isUpgradeMilestonePurchase(cell.enhancementLevel);
    const displayIndex = cellIndex + 1;
    return {
      isVisible: true,
      isAffordable: affordable,
      isMilestoneReady: milestoneReady,
      costText:
        blockedReason !== undefined
          ? formatHovGainUpgradeBlockedActionCost(blockedReason)
          : formatHovGainUpgradeActionCost({
              cost: cell.upgradeCost,
              purchaseCount: batchSummary?.purchaseCount,
              simultaneousPurchase: toSimultaneousPurchase(simultaneousPreview),
            }),
      tooltip: createLazyHovGainUpgradeTooltipViewModel(() => {
        if (blockedReason !== undefined) {
          return buildHovGainUpgradeWallBlockedTooltipViewModel({
            kind: cellTooltipKind(cellIndex),
            enhancementLevel: cell.enhancementLevel,
            reason: blockedReason,
          });
        }
        const tooltipBatchPreview = resolveTooltipBatchPreview(
          batchSummary,
          () =>
            options.resolveHovCellOneCostBatchPreview?.(
              displayIndex as HovCellGainUpgradeIndex,
            ),
        );
        return buildHovGainUpgradeTooltipViewModel({
          kind: cellTooltipKind(cellIndex),
          currentHov: snapshot.hov,
          cost: cell.upgradeCost,
          canPurchase: affordable,
          currentValue: cell.effectiveHovGainPerSecond,
          nextValue:
            simultaneousPreview?.valueAfter ??
            resolveTooltipBatchValueAfter(tooltipBatchPreview) ??
            cell.effectiveHovGainPerSecondAfterUpgrade,
          effect: resolveTooltipEffect(milestoneReady, tooltipBatchPreview),
          purchaseCount: tooltipBatchPreview?.purchaseCount,
          simultaneousPurchase: toSimultaneousPurchase(simultaneousPreview),
          enhancementLevel: cell.enhancementLevel,
        });
      }),
    };
  }
}

function toSimultaneousPurchase(
  preview: HovGainUpgradeSimultaneousPurchasePreview | undefined,
):
  | { readonly purchaseCount: number; readonly totalCost: HugeNumber }
  | undefined {
  if (preview === undefined) return undefined;
  return {
    purchaseCount: preview.purchaseCount,
    totalCost: preview.spentHov,
  };
}

type HovGainUpgradeOneCostBatchTooltipPreview =
  | HovGainUpgradeOneCostBatchPreview
  | HovGainUpgradeOneCostBatchSummary;

function resolveTooltipBatchPreview(
  batchSummary: HovGainUpgradeOneCostBatchSummary | undefined,
  resolvePreview:
    | (() => HovGainUpgradeOneCostBatchPreview | undefined)
    | undefined,
): HovGainUpgradeOneCostBatchTooltipPreview | undefined {
  if (batchSummary === undefined) {
    return undefined;
  }
  if (resolvePreview === undefined) {
    return batchSummary;
  }
  return resolvePreview();
}

function resolveTooltipBatchValueAfter(
  preview: HovGainUpgradeOneCostBatchTooltipPreview | undefined,
): HugeNumber | undefined {
  return preview !== undefined && "valueAfter" in preview
    ? preview.valueAfter
    : undefined;
}

function cellTooltipKind(cellIndex: number): HovGainUpgradeTooltipKind {
  return { type: "cell", displayIndex: cellIndex + 1 };
}

function resolveTooltipEffect(
  milestoneReady: boolean,
  batchPreview: { readonly purchaseCount: number } | undefined = undefined,
) {
  if (batchPreview !== undefined) {
    return {
      type: "enhancement-levels" as const,
      count: batchPreview.purchaseCount,
    };
  }

  return milestoneReady
    ? {
        type: "multiplier" as const,
        multiplier: formatHovGainUpgradeMilestoneMultiplier(),
      }
    : { type: "delta" as const };
}

function invisibleRow(): GroupingHovGainUpgradeRowState {
  return {
    isVisible: false,
    isAffordable: false,
    isMilestoneReady: false,
    costText: "",
    tooltip: {
      title: "",
      region: "",
      summary: "",
      effectLabel: "",
      effect: "",
      afterLabel: "",
      after: "",
      costLabel: "",
      cost: "",
      stateLabel: "",
      state: "",
    },
  };
}
